import React, { forwardRef } from 'react';

import { Slot, Slottable } from '@radix-ui/react-slot';

import { cn } from 'common/style/utils';
import { buttonVariants, getStyleWithTint } from 'common/uiV3/Button/buttonVariants';
import Spinner from 'common/uiV3/Spinner';

import type { VariantProps } from 'tailwind-variants';

export type ButtonProps = React.ComponentPropsWithoutRef<'button'> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
    isLoading?: boolean;
    tintColor?: string | null;
    'aria-label'?: string;
  };

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant,
      size,
      color,
      asChild = false,
      isLoading,
      disabled,
      tintColor,
      style,
      children,
      'aria-label': ariaLabel,
      iconOnly,
      className,
      fullWidth,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : 'button';
    const styles = buttonVariants({
      variant,
      color: tintColor ? undefined : color,
      size,
      disabled: !!disabled,
      isLoading: !!isLoading,
      isTinted: !!tintColor,
      iconOnly: !!iconOnly,
      fullWidth: !!fullWidth,
    });

    const showChildren = !iconOnly || (iconOnly && !isLoading);

    return (
      <Comp
        ref={ref as any}
        data-slot="button"
        aria-label={ariaLabel}
        aria-disabled={disabled || isLoading}
        disabled={disabled || isLoading}
        style={getStyleWithTint(style, tintColor)}
        className={cn(styles.base(), className)}
        {...props}>
        {isLoading && <Spinner />}
        {showChildren && <Slottable>{children}</Slottable>}
      </Comp>
    );
  },
);

export default Button;
